import * as React from 'react'
import {
  Drawer,
  Box,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { CSSObject, Theme, styled } from '@mui/material/styles'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ChevronLeft,
  ChevronRight,
  Assessment,
  Inventory2Outlined,
  Assignment,
  Analytics,
  LibraryBooks,
  AttachMoney,
  Email,
} from '@mui/icons-material'
import { useIsMobile } from 'lib/utils/useIsMobile'
import {
  ASSET_REPORTS,
  ANALYTICS,
  CATALOG,
  INVENTORY_REPORT,
  LOCATION_MANAGEMENT,
  RECEIVE,
  PATIENT_REPORT,
  REFERRING_PHYSICIANS,
  LABOR_COST,
  SCRUB_TECHNICIANS,
  PRODUCT_EMAIL_SERVICE,
} from 'app/ROUTES_CONSTANTS'
import { useIsRouteActive } from 'lib/utils/isRouteActive'
import { ReceivingSalesRepIcon } from 'assets/icons/ReceivingSalesRepIcon'
import { PreferenceCardIcon } from 'assets/icons/PreferenceCardIcon'
import PatientsReportIcon from 'assets/icons/images/PatientsReportEnabledIcon.png'
import PatientsReportDisabledIcon from 'assets/icons/images/PatientsReportDisabledIcon.svg'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useOrganizationSettings } from 'lib/context/OrganizationSettingsContext'

const DRAWER_WIDTH = 240

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: 500,
  position: 'inherit',
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}))

interface DashboardSideBarProps {
  sidebarWidth: number
}

export const DashboardSideBar = ({ sidebarWidth }: DashboardSideBarProps) => {
  const flags = useFlags()
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  const isScannerPage = location.pathname.includes('scan')
  const { isActive } = useIsRouteActive()
  const { physiciansTitle, showScrubTechnician } = useOrganizationSettings()

  // This is only for scanning into receiving.
  // The screen is too small to have a side bar when scanning.
  const hideSideBar = isMobile && isScannerPage

  const handleDrawerToggle = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const handleNavigation = (route: string) => {
    setIsMenuOpen(false)
    navigate(route)
  }

  const navigateAnalytics = () => {
    handleNavigation(`/${ANALYTICS}`)
  }

  const navigateCatalog = () => {
    handleNavigation(`/${CATALOG}`)
  }

  const navigateProductEmailService = () => {
    handleNavigation(`/${PRODUCT_EMAIL_SERVICE}`)
  }

  // const navigateAuditLogs = () => {
  //   handleNavigation('/audit-logs')
  // }

  // const navigateManageReps = () => {
  //   handleNavigation('/manage-reps')
  // }

  const navigateReceive = () => {
    handleNavigation(`/${RECEIVE}`)
  }

  const navigateInventoryReport = () => {
    handleNavigation(`/${INVENTORY_REPORT}`)
    // resetFilters()
  }

  const navigateLocationsManagement = () => {
    handleNavigation(`/${LOCATION_MANAGEMENT}`)
  }

  const navigatePreferenceCards = () => {
    handleNavigation('/preference-cards')
  }

  const navigateUsageReport = () => {
    handleNavigation(`/${PATIENT_REPORT}`)
  }

  const navigateAssetReport = () => {
    handleNavigation(`/${ASSET_REPORTS}`)
  }

  type TabLabels =
    | '$receiveProducts'
    | '$analytics'
    | '$catalog'
    | '$inventoryReport'
    | '$preferenceCards'
    | '$locationsManagement'
    | '$patientsReport'
    | '$laborCost'
    | '$assetReports'
    | '$referringPhysicians'
    | '$scrubTechnicians'
    | '$productEmailService'
  const Tabs: {
    [K in TabLabels]:
      | {
          label: string
          icon: JSX.Element
          onClick: () => void
          isVisible?: boolean
        }
      | undefined
  } = {
    $analytics: {
      label: 'Analytics',
      icon: <Analytics color={isActive(ANALYTICS) ? 'primary' : 'action'} />,
      onClick: navigateAnalytics,
      isVisible: flags.analyticsDashboard,
    },
    $catalog: {
      label: 'Catalog',
      icon: <LibraryBooks color={isActive(CATALOG) ? 'primary' : 'action'} />,
      onClick: navigateCatalog,
      isVisible: flags.catalogPluginEnabled,
    },
    $laborCost: {
      label: 'Labor Cost',
      icon: <AttachMoney color={isActive(LABOR_COST) ? 'primary' : 'action'} />,
      onClick: () => handleNavigation(`/${LABOR_COST}`),
      isVisible: flags.adminDashboardSideNavigationLaborCost,
    },
    $productEmailService: {
      label: 'Product Email Service',
      icon: (
        <Email color={isActive(PRODUCT_EMAIL_SERVICE) ? 'primary' : 'action'} />
      ),
      onClick: navigateProductEmailService,
      isVisible: flags.newImplantSurgeryReportSender,
    },
    $assetReports: {
      label: 'Product Details',
      icon: (
        <Assignment color={isActive(ASSET_REPORTS) ? 'primary' : 'action'} />
      ),
      onClick: navigateAssetReport,
      isVisible: flags.adminDashboardSideNavigationProductDetailsLink,
    },
    // $auditLogs: {
    //   label: 'Audit Logs',
    //   icon: (
    //     <Description
    //       color={isRouteActive('audit-logs') ? 'primary' : 'action'}
    //     />
    //   ),
    //   onClick: navigateAuditLogs,
    // },
    // $manageReps: {
    //   label: 'Manage Reps',
    //   icon: (
    //     <ManageAccounts
    //       color={isRouteActive('manage-reps') ? 'primary' : 'action'}
    //     />
    //   ),
    //   onClick: navigateManageReps,
    // },
    $receiveProducts: {
      label: 'Receive Products',
      icon: <ReceivingSalesRepIcon active={isActive(`${RECEIVE}`)} dark />,
      onClick: navigateReceive,
      isVisible: flags.adminDashboardSideNavigationReceiveProducts,
    },
    $inventoryReport: {
      label: 'Inventory Report',
      icon: (
        <Assessment
          color={isActive('inventory-report') ? 'primary' : 'action'}
        />
      ),
      onClick: navigateInventoryReport,
      isVisible: flags.adminDashboardSideNavigationInventoryReport,
    },
    $preferenceCards: {
      label: 'Preference Cards',
      icon: <PreferenceCardIcon active={isActive('preference-cards')} />,
      onClick: navigatePreferenceCards,
      isVisible: flags.adminDashboardSideNavigationPreferenceCardsLink,
    },
    $patientsReport: {
      label: 'Patients Report',
      icon: (
        <img
          src={
            isActive(PATIENT_REPORT)
              ? PatientsReportIcon
              : PatientsReportDisabledIcon
          }
          alt="Patients Report"
          height="22"
          width="auto"
        />
      ),
      onClick: navigateUsageReport,
      isVisible: flags.adminDashboardSideNavigationUsageReportsLink,
    },
    $locationsManagement: {
      label: 'Inventory Locations',
      icon: (
        <Inventory2Outlined
          color={isActive('location-management') ? 'primary' : 'action'}
        />
      ),
      onClick: navigateLocationsManagement,
      isVisible: flags.adminDashboardSideNavigationInventoryLocations,
    },
    $referringPhysicians: {
      label: physiciansTitle,
      icon: (
        <img
          src={
            isActive(REFERRING_PHYSICIANS)
              ? '/physicianActive.svg'
              : '/physician.svg'
          }
          alt="physician"
          height="24"
        />
      ),
      onClick: () => handleNavigation(`/${REFERRING_PHYSICIANS}`),
    },

    $scrubTechnicians: showScrubTechnician
      ? {
          label: 'Scrub Technicians',
          icon: (
            <img
              src={
                isActive(SCRUB_TECHNICIANS)
                  ? '/physicianActive.svg'
                  : '/physician.svg'
              }
              alt="physician"
              height="24"
            />
          ),
          onClick: () => handleNavigation(`/${SCRUB_TECHNICIANS}`),
        }
      : undefined,
  }

  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: isMenuOpen ? 'flex-end' : 'center',
    ...theme.mixins.toolbar,
  }))

  return (
    <Box sx={{ display: 'flex' }}>
      {hideSideBar ? null : (
        <StyledDrawer
          variant="permanent"
          open={isMenuOpen}
          sx={{
            position: 'static',
            '& .MuiPaper-root': {
              minHeight: `calc(100vh - ${sidebarWidth}px)`,
              paddingTop: `${sidebarWidth}px`,
            },
          }}
        >
          <DrawerHeader>
            <IconButton onClick={handleDrawerToggle}>
              {isMenuOpen ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            {Object.entries(Tabs).map(([key, tab]) => {
              if (!tab) return null
              const { label, icon, onClick, isVisible } = tab
              if (isVisible === false) {
                return null
              }

              return (
                <ListItem key={key} disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: isMenuOpen ? 'initial' : 'center',
                      px: 2.5,
                    }}
                    onClick={onClick}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: isMenuOpen ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={label}
                      sx={{ opacity: isMenuOpen ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
          </List>
        </StyledDrawer>
      )}
    </Box>
  )
}
